import { blogRootPathname } from '@config'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import ContentfulBlogTagLabel from '@entries/contentful-blog-tag-label'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import { EnhancedLinkUnstyled } from '@ui/enhanced-components/enhanced-components'
import { graphql } from 'gatsby'
import React from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: 'auto',
  height: '100%',
}

const ContentfulBlogArticleCard: Utils.EntryFC<
  Queries.ContentfulBlogArticleCardFragment
> = ({
  content: { title, image, slug, tags, introduction },
  as = 'h2',
  fieldInspectorProps,
}) => {
  const url = `${blogRootPathname}/${slug}`

  const Component = as

  return (
    <article className="flex w-full cursor-pointer gap-8 border-b border-solid border-b-grey-100 py-8 text-left last-of-type:border-none laptop:py-12">
      <div
        className="h-16 w-16 min-w-16 flex-none overflow-hidden rounded-md laptop:h-[154px] laptop:w-[154px] laptop:min-w-[154px]"
        {...fieldInspectorProps({
          fieldId: 'image',
        })}
      >
        <EnhancedLinkUnstyled href={url} tabIndex={-1}>
          <AssetOptionsContext.Provider value={assetOptions}>
            <Entry
              content={image}
              mapping={{
                [ContentType.ContentfulAsset]: ContentfulAsset,
              }}
            />
          </AssetOptionsContext.Provider>
        </EnhancedLinkUnstyled>
      </div>
      <div className="flex grow flex-col gap-8 overflow-hidden">
        <div>
          <EnhancedLinkUnstyled className="hover:!no-underline" href={url}>
            <Component
              className="mb-2 text-110 font-medium"
              {...fieldInspectorProps({
                fieldId: 'title',
              })}
            >
              {title}
            </Component>
          </EnhancedLinkUnstyled>

          {introduction && (
            <EnhancedLinkUnstyled href={url} tabIndex={-1}>
              <p
                {...fieldInspectorProps({
                  fieldId: 'introduction',
                })}
                className="line-clamp-5 text-085 tablet:line-clamp-3"
              >
                {introduction.text}
              </p>
            </EnhancedLinkUnstyled>
          )}
        </div>
        <span
          className="truncate [&>:not(:first-child)]:before:pr-1 [&>:not(:first-child)]:before:content-dot [&>:not(:last-child)]:pr-1"
          {...fieldInspectorProps({
            fieldId: 'tags',
          })}
        >
          {(tags || []).map((tag, index) => (
            <EnhancedLinkUnstyled key={index} href={url} tabIndex={-1}>
              <Entry
                content={tag}
                mapping={{
                  [ContentType.ContentfulBlogTag]: ContentfulBlogTagLabel,
                }}
              />
            </EnhancedLinkUnstyled>
          ))}
        </span>
      </div>
    </article>
  )
}

export const query = graphql`
  fragment ContentfulBlogArticleCard on ContentfulBlogArticle {
    ...ContentfulEntry
    title
    slug
    tags {
      ...ContentfulBlogTagLabel
    }
    introduction {
      text
    }
    image {
      ...Asset
      gatsbyImageData(
        placeholder: BLURRED
        height: 300
        formats: [AVIF, WEBP, AUTO]
      )
    }
  }
`

export default withDisplayName(
  ContentfulBlogArticleCard,
  'ContentfulBlogArticleCard'
)
